import React, { useState, useEffect, useCallback } from 'react';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import { useUsuario } from '../../contexts/UsuarioContext';
import { Departamento, Usuario } from '../../types';
import departamentoService from '../../services/departamentoService';
import { toast } from 'react-toastify';

const GerenciamentoDepartamentos: React.FC = () => {
  const { departamentos } = useDepartamento();
  const { usuarios: todosUsuarios } = useUsuario();

  const [departamentoSelecionado, setDepartamentoSelecionado] = useState<Departamento | null>(null);
  const [lideres, setLideres] = useState<Usuario[]>([]);
  const [membros, setMembros] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novoLiderId, setNovoLiderId] = useState('');

  const carregarDetalhesDepartamento = useCallback(async (departamento: Departamento) => {
    setCarregando(true);
    try {
      const resposta = await departamentoService.obterPorId(departamento.id);
      if (resposta.success && resposta.departamento) {
        setLideres((resposta.departamento.lideres as unknown as Usuario[]) || []);
      }
    } catch {
      toast.error('Erro ao carregar detalhes do departamento.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (departamentoSelecionado) {
      carregarDetalhesDepartamento(departamentoSelecionado);
      const membrosDoDepartamento = todosUsuarios.filter((u: Usuario) =>
        u.departamentoId === departamentoSelecionado.id ||
        u.departamento_id === departamentoSelecionado.id
      );
      setMembros(membrosDoDepartamento);
    }
  }, [departamentoSelecionado, todosUsuarios, carregarDetalhesDepartamento]);

  const adicionarLider = async () => {
    if (!departamentoSelecionado || !novoLiderId || salvando) return;
    setSalvando(true);
    try {
      const resposta = await departamentoService.adicionarLider(departamentoSelecionado.id, novoLiderId);
      if (resposta.success) {
        await carregarDetalhesDepartamento(departamentoSelecionado);
        setNovoLiderId('');
        toast.success(`Líder adicionado ao departamento ${departamentoSelecionado.nome}.`);
      } else {
        toast.error(resposta.message || 'Erro ao adicionar líder.');
      }
    } catch {
      toast.error('Erro ao adicionar líder.');
    } finally {
      setSalvando(false);
    }
  };

  const removerLider = async (liderId: string) => {
    if (!departamentoSelecionado || salvando) return;
    setSalvando(true);
    try {
      const resposta = await departamentoService.removerLider(departamentoSelecionado.id, liderId);
      if (resposta.success) {
        setLideres(prev => prev.filter(l => l.id !== liderId));
        toast.success(`Líder removido do departamento ${departamentoSelecionado.nome}.`);
      } else {
        toast.error(resposta.message || 'Erro ao remover líder.');
      }
    } catch {
      toast.error('Erro ao remover líder.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        Gerenciamento de Departamentos
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Lista de Departamentos */}
        <div className="col-span-1 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-3 text-gray-700">Departamentos</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {departamentos.map(departamento => (
              <div
                key={departamento.id}
                className={`p-3 rounded-md cursor-pointer ${
                  departamentoSelecionado?.id === departamento.id
                    ? 'bg-blue-100 border-l-4 border-blue-500'
                    : 'hover:bg-gray-100'
                }`}
                onClick={() => setDepartamentoSelecionado(departamento)}
              >
                <p className="font-medium">{departamento.nome}</p>
                <p className="text-xs text-gray-500">
                  {todosUsuarios.filter((u: Usuario) =>
                    u.departamentoId === departamento.id || u.departamento_id === departamento.id
                  ).length} membros •{' '}
                  {Array.isArray(departamento.lideres) ? departamento.lideres.length : 0}
                  {Array.isArray(departamento.lideres) && departamento.lideres.length === 1 ? ' líder' : ' líderes'}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Detalhes do Departamento */}
        <div className="col-span-2 bg-gray-50 p-4 rounded-lg">
          {departamentoSelecionado ? (
            <>
              <h3 className="text-lg font-semibold mb-3 text-gray-700">{departamentoSelecionado.nome}</h3>

              {/* Líderes */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-2">Líderes</h4>
                {carregando ? (
                  <p className="text-gray-400 italic text-sm">Carregando...</p>
                ) : lideres.length > 0 ? (
                  <div className="space-y-2">
                    {lideres.map(lider => (
                      <div key={lider.id} className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm">
                        <div className="flex items-center">
                          <img src="/profilepicture.png" alt={lider.nome} className="w-8 h-8 rounded-full mr-3" />
                          <div>
                            <p className="font-medium">{lider.nome}</p>
                            <p className="text-xs text-gray-500">{lider.cargo}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removerLider(lider.id)}
                          disabled={salvando}
                          className="text-red-600 hover:text-red-800 disabled:opacity-40"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">Nenhum líder designado para este departamento</p>
                )}
              </div>

              {/* Adicionar Novo Líder */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Adicionar Novo Líder</h4>
                <div className="flex space-x-2">
                  <select
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={novoLiderId}
                    onChange={(e) => setNovoLiderId(e.target.value)}
                  >
                    <option value="">Selecione um usuário</option>
                    {membros
                      .filter((u: Usuario) => !lideres.some(l => l.id === u.id))
                      .sort((a, b) => a.nome.localeCompare(b.nome))
                      .map((u: Usuario) => (
                        <option key={u.id} value={u.id}>
                          {u.nome} ({u.cargo || 'Membro'})
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={adicionarLider}
                    disabled={!novoLiderId || salvando}
                    className={`px-4 py-2 rounded-md ${
                      novoLiderId && !salvando
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {salvando ? 'Salvando...' : 'Adicionar'}
                  </button>
                </div>
              </div>

              {/* Membros */}
              <div className="mt-6">
                <h4 className="font-medium text-gray-700 mb-2">Membros</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {membros.map((u: Usuario) => (
                    <div key={u.id} className="flex items-center p-2 bg-white rounded-md shadow-sm">
                      <img src="/profilepicture.png" alt={u.nome} className="w-8 h-8 rounded-full mr-3" />
                      <div>
                        <p className="font-medium">{u.nome}</p>
                        <p className="text-xs text-gray-500">{u.cargo}</p>
                      </div>
                      {lideres.some(l => l.id === u.id) && (
                        <span className="ml-auto bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-0.5 rounded">
                          Líder
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
              </svg>
              <p>Selecione um departamento para visualizar seus detalhes</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GerenciamentoDepartamentos;
